import type { GeneratedFileTemplate } from "schemas/generated/fileTemplate";
import type { TemplateItem } from "../CreateNewPage";

import React, { useState } from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import IconButton from "@replo/design-system/components/button/IconButton";
import SelectableChip from "@replo/design-system/components/chip/SelectableChip";
import { skipToken } from "@tanstack/react-query";
import { LandPlot, ShoppingBag, Sparkles } from "lucide-react";

import {
  FileTemplatesList,
  GeneratedFileTemplatesList,
} from "./TemplatePreviewList";

type PageCategoryChip = {
  icon: React.ElementType;
  title: string;
  category: string;
};

const PAGE_CATEGORIES: PageCategoryChip[] = [
  { icon: LandPlot, title: "Landing", category: "Landing Page" },
  { icon: ShoppingBag, title: "Product", category: "Product" },
];

interface CreatePageMenuProps {
  selectedPageTemplate: TemplateItem | null;
  setSelectedPageTemplate: (template: TemplateItem | null) => void;
}

function CreatePageMenu({
  selectedPageTemplate,
  setSelectedPageTemplate,
}: CreatePageMenuProps) {
  const projectId = useCurrentProjectId();
  const [searchString, setSearchString] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [generatedFileTemplates, setGeneratedFileTemplates] = useState<
    GeneratedFileTemplate[]
  >([]);

  const { data: pageTemplates, isLoading: isLoadingPageTemplates } =
    trpc.fileTemplate.find.useQuery(
      projectId
        ? {
            projectId,
            type: "page",
            category: selectedCategory ?? undefined,
            options: { page: 0, pageSize: 15, onlyGlobalTemplates: true },
          }
        : skipToken,
    );

  const {
    data: relevantPageTemplates,
    isLoading: isLoadingRelevantPageTemplates,
  } = trpc.fileTemplate.findRelevant.useQuery(
    projectId && searchString.length > 0
      ? {
          projectId,
          type: "page",
          category: selectedCategory ?? undefined,
          searchTerm: searchString,
          options: { page: 0, pageSize: 6 },
        }
      : skipToken,
  );

  const {
    mutate: generateFileTemplates,
    isPending: isGeneratingFileTemplates,
  } = trpc.fileTemplate.generateFromPrompt.useMutation({
    onSuccess: (generatedTemplates) => {
      setGeneratedFileTemplates(generatedTemplates);
    },
  });

  const handlePageTemplateClick = (templateId: string) => {
    if (selectedPageTemplate?.id === templateId) {
      setSelectedPageTemplate(null);
    } else {
      const allTemplates =
        searchString.length > 0 ? relevantPageTemplates : pageTemplates;
      const template = allTemplates?.find((t) => t.id === templateId);
      if (template) {
        setSelectedPageTemplate(template);
      }
    }
  };

  const handleGeneratedPageTemplateClick = (templateId: string) => {
    const template = generatedFileTemplates.find((t) => t.id === templateId);
    if (template) {
      setSelectedPageTemplate(template);
    }
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(selectedCategory === category ? null : category);
  };

  const handleGenerateFileTemplates = () => {
    if (!projectId || !searchString.trim()) {
      return;
    }

    setGeneratedFileTemplates([]);

    generateFileTemplates({
      projectId,
      prompt: searchString.trim(),
      numberOfPages: 2,
    });
  };

  const filteredPageTemplates = React.useMemo(() => {
    const templates =
      searchString.length > 0 ? relevantPageTemplates : pageTemplates;

    return templates;
  }, [pageTemplates, relevantPageTemplates, searchString]);

  const showGeneratedFileTemplatesSection =
    generatedFileTemplates.length > 0 || isGeneratingFileTemplates;

  return (
    <div className="flex flex-col h-full gap-6 no-scrollbar">
      <div className="flex flex-col gap-3">
        <DebouncedInput
          placeholder="Search templates or tell us what you want..."
          size="base"
          value={searchString}
          onValueChange={setSearchString}
          endEnhancer={() => (
            <IconButton
              size="sm"
              variant="primary"
              icon={<Sparkles size={12} />}
              isLoading={isGeneratingFileTemplates}
              disabled={!searchString.trim() || isGeneratingFileTemplates}
              onClick={handleGenerateFileTemplates}
            />
          )}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !isGeneratingFileTemplates &&
              Boolean(searchString.trim())
            ) {
              handleGenerateFileTemplates();
            }
          }}
        />

        <div className="flex gap-2 flex-wrap">
          {PAGE_CATEGORIES.map(({ icon: Icon, title, category }) => (
            <SelectableChip
              key={category}
              size="base"
              active={selectedCategory === category}
              onChange={() => handleCategoryClick(category)}
              startEnhancer={<Icon size={16} />}
            >
              {title}
            </SelectableChip>
          ))}
        </div>
      </div>

      {showGeneratedFileTemplatesSection && (
        // eslint-disable-next-line replo/no-tailwind-literal-color
        <div className="bg-indigo-50 p-4 rounded-lg flex flex-col gap-3">
          <div className="flex items-center gap-1.5 header-h4 text-muted">
            <Sparkles size={16} />
            <span>Generated</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <GeneratedFileTemplatesList
              generatedTemplates={generatedFileTemplates}
              isLoading={isGeneratingFileTemplates}
              selectedPageTemplate={selectedPageTemplate}
              handlePageTemplateClick={handleGeneratedPageTemplateClick}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-20">
        <FileTemplatesList
          fileTemplates={filteredPageTemplates}
          isLoading={isLoadingRelevantPageTemplates || isLoadingPageTemplates}
          selectedPageTemplate={selectedPageTemplate}
          handlePageTemplateClick={handlePageTemplateClick}
        />
      </div>
    </div>
  );
}

export default CreatePageMenu;
